import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import configment from '@adc/configment'
import { loadStripe } from '@stripe/stripe-js/pure'
import { noop } from 'lodash'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import classnames from 'classnames/bind'
import { Button, Loader, Text } from '../../components/elementals'
import { ColorNames } from '../../constants/colorVars'
import styles from './styles.scss'
import {
  StripeError,
  StripePaymentElementChangeEvent,
  TermOption,
} from '@stripe/stripe-js'

const cx = classnames.bind(styles)
const ARBITRARY_UX_ERROR_PREVENTION_THRESHOLD = 3
const ADC_STRIPE_TERMS_LINK = 'https://stripe.com/legal/consumer'
const ADC_STRIPE_PRIVACY_LINK = 'https://stripe.com/privacy'

export interface StripePaymentHookInput {
  clientSecret: string
  isLoading: boolean
  isSetup: boolean
  message: string
  confirmPayment?: () => void
  paymentErrorHandler?: (e?: StripeError) => void
  customSubmitButton?: boolean
  paymentDisabled?: boolean
  customCTALabel?: string
  onClickCTA?: () => void
  additionalInfo?: React.ReactNode
}
export type FormValues = {
  isProcessing: boolean
  isDisabled?: boolean
  isSubmitDisabled?: boolean
  handleSubmit: (e?: FormEvent) => Promise<void>
}

export type StripePaymentHookOutput = {
  StripePaymentSetupForm: JSX.Element
} & FormValues

interface CheckoutFormHookInput {
  confirmPayment?: () => void
  paymentErrorHandler?: (e?: StripeError) => void
  setFormValues: (formValues: FormValues) => void
  customSubmitButton?: boolean
  paymentDisabled?: boolean
  isSetup?: boolean
  customCTALabel?: string
  onClickCTA?: () => void
  additionalInfo?: React.ReactNode
}

export const CheckoutForm = ({
  confirmPayment,
  paymentErrorHandler,
  customSubmitButton = false,
  setFormValues,
  paymentDisabled,
  customCTALabel,
  onClickCTA,
  additionalInfo,
  isSetup = false,
}: CheckoutFormHookInput) => {
  const stripe = useStripe()
  const elements = useElements()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
  // const notifications = useApplicationNotifications()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFormComplete, setIsFormComplete] = useState(false)
  const [errorCount, setErrorCount] = useState<number>(0)

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault()
      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return
      }

      setIsProcessing(true)

      const { error } = await stripe[
        isSetup ? 'confirmSetup' : 'confirmPayment'
      ]({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/`,
        },
        redirect: 'if_required',
      })

      setIsProcessing(false)

      if (error) {
        const currentErrorCount = errorCount + 1
        setErrorCount(currentErrorCount)

        if (currentErrorCount >= ARBITRARY_UX_ERROR_PREVENTION_THRESHOLD) {
          // @ts-ignore this will be handled differently once we decide which errors are retryable and which don't
          error.type = 'non_retryable_error'
        }
        paymentErrorHandler && paymentErrorHandler(error)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        // Leaving this commented in case after release we realize we want toasts
        // notifications?.toast.open({
        //   // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        //   role: NOTIFICATION_ROLES.ERROR,
        //   title: 'Payment attempt was unsuccessful.',
        //   message:
        //     'Please try again or contact our support team for further assistance.',
        // })
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        // Leaving this commented in case after release we realize we want toasts
        // notifications?.toast.open({
        //   // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        //   role: NOTIFICATION_ROLES.SUCCESS,
        //   title: 'Your payment was processed successfully',
        // })
        confirmPayment && confirmPayment()
      }
    },
    [confirmPayment, elements, paymentErrorHandler, stripe, errorCount, isSetup]
  )

  const isDisabled = useMemo(
    () => isProcessing || !stripe || !elements || paymentDisabled,
    [elements, isProcessing, paymentDisabled, stripe]
  )

  const isSubmitDisabled = useMemo(
    () => isDisabled || !isFormComplete,
    [isDisabled, isFormComplete]
  )
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    setFormValues({ isDisabled, isSubmitDisabled, isProcessing, handleSubmit })
  }, [isDisabled, handleSubmit, isProcessing, setFormValues, isSubmitDisabled])

  const neverTermOption: TermOption = 'never'

  const options = useMemo(
    () => ({
      readOnly: isDisabled,
      terms: {
        card: neverTermOption,
        googlePay: neverTermOption,
        applePay: neverTermOption,
        usBankAccount: neverTermOption,
      },
    }),
    [isDisabled]
  )
  useEffect(() => {
    const paymentElement = elements && elements.getElement('payment')
    paymentElement?.update(options)
  }, [isDisabled, elements, options])

  const handleOnPaymentElementChange = useCallback(
    (e: StripePaymentElementChangeEvent) => setIsFormComplete(e.complete),
    []
  )

  // Doing this to apply any updates that may have been made during loading time and lost.
  const onPaymentElementReady = useCallback(() => {
    const paymentElement = elements && elements.getElement('payment')
    paymentElement?.update(options)
  }, [elements, options])

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form
        id='payment-form'
        onSubmit={handleSubmit}
        className={cx({ 'payment-disabled': isDisabled })}
      >
        <PaymentElement
          id='payment-element'
          options={options}
          onChange={handleOnPaymentElementChange}
          onReady={onPaymentElementReady}
        />
        <Text.Body className={cx('terms')}>
          Payment provider{' '}
          <a href={ADC_STRIPE_TERMS_LINK} target='_blank' rel='noreferrer'>
            Terms
          </a>{' '}
          |{' '}
          <a href={ADC_STRIPE_PRIVACY_LINK} target='_blank' rel='noreferrer'>
            Privacy
          </a>
        </Text.Body>
        {!customSubmitButton && (
          <div className={cx('submit-button-container')}>
            <Button
              elmId='stripe_payment_submit'
              className={cx('payment-submit-button')}
              disabled={isSubmitDisabled}
              onClick={onClickCTA}
              id='submit'
              isStretch
              role='primary'
              color={ColorNames.Info}
            >
              {isProcessing ? 'Processing ... ' : customCTALabel || 'Pay Now'}
            </Button>
            {Boolean(additionalInfo) && additionalInfo}
          </div>
        )}
      </form>
    </>
  )
}

const useStripePayment = ({
  clientSecret,
  isLoading,
  message,
  confirmPayment,
  paymentErrorHandler,
  customCTALabel,
  onClickCTA,
  additionalInfo,
  customSubmitButton = false,
  paymentDisabled = false,
  isSetup,
}: StripePaymentHookInput) => {
  const stripePublishableKey = configment.get('STRIPE_PUBLISHABLE_KEY')
  const stripePromise = useMemo(
    () => loadStripe(stripePublishableKey as string),
    [stripePublishableKey]
  )
  const [formValues, setFormValuesState] = useState<FormValues>({
    isProcessing: false,
    isDisabled: false,
    isSubmitDisabled: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    // @ts-ignore initial value is noop
    handleSubmit: noop,
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const StripePaymentSetupForm = useMemo(() => {
    return (
      <>
        {isLoading ? (
          <Loader elmId='stripe_spinner' active={isLoading} inverted />
        ) : (
          <>
            {clientSecret && stripePromise && (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret }}
                key={clientSecret}
              >
                <CheckoutForm
                  setFormValues={setFormValuesState}
                  confirmPayment={confirmPayment}
                  customSubmitButton={customSubmitButton}
                  paymentErrorHandler={paymentErrorHandler}
                  paymentDisabled={paymentDisabled}
                  customCTALabel={customCTALabel}
                  onClickCTA={onClickCTA}
                  additionalInfo={additionalInfo}
                  isSetup={isSetup}
                />
              </Elements>
            )}
            {message && <div>{message}</div>}
          </>
        )}
      </>
    )
  }, [
    additionalInfo,
    clientSecret,
    confirmPayment,
    customCTALabel,
    customSubmitButton,
    isLoading,
    isSetup,
    message,
    onClickCTA,
    paymentDisabled,
    paymentErrorHandler,
    stripePromise,
  ])

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  return useMemo(
    () => ({ StripePaymentSetupForm, ...formValues }),
    [StripePaymentSetupForm, formValues]
  )
}

export default useStripePayment
